/**
 * 1:1 port of packages/amgsicbo_v2/lib/core/utils/result_parser.dart.
 *
 * Input format: "d1,d2,d3" — three integers (1..6) separated by commas.
 *   e.g. "2,4,6"
 */
import type { DiceCategory } from '../types/game';

export interface ParsedResult {
  dice1: number;
  dice2: number;
  dice3: number;
  total: number;
  isTriple: boolean;
  category: DiceCategory;
}

export function parseResultString(raw: string): ParsedResult {
  const parts = raw
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p.length > 0);

  if (parts.length < 3) {
    throw new Error(`Invalid SicBo result format: ${raw}`);
  }

  const dice1 = parseDie(parts[0]);
  const dice2 = parseDie(parts[1]);
  const dice3 = parseDie(parts[2]);

  const total = dice1 + dice2 + dice3;
  const isTriple = dice1 === dice2 && dice2 === dice3;

  let category: DiceCategory;
  if (isTriple) category = 'triple';
  else if (total >= 4 && total <= 10) category = 'small';
  else category = 'big';

  return { dice1, dice2, dice3, total, isTriple, category };
}

function parseDie(s: string): number {
  const m = /\d+/.exec(s);
  if (!m) throw new Error(`Invalid die: ${s}`);
  const n = parseInt(m[0], 10);
  if (n < 1 || n > 6) throw new Error(`Die out of range: ${n}`);
  return n;
}

/** Number of dice showing `value`. Used for Single payouts. */
export function countOf(r: ParsedResult, value: number): number {
  let n = 0;
  if (r.dice1 === value) n++;
  if (r.dice2 === value) n++;
  if (r.dice3 === value) n++;
  return n;
}

/** Both `a` and `b` appear in the result. Used for Combo payouts. */
export function hasCombo(r: ParsedResult, a: number, b: number): boolean {
  const has = (v: number): boolean =>
    r.dice1 === v || r.dice2 === v || r.dice3 === v;
  return has(a) && has(b);
}

export function isOdd(r: ParsedResult): boolean {
  return r.total % 2 === 1;
}
export function isEven(r: ParsedResult): boolean {
  return r.total % 2 === 0;
}
